import { useInboxDraftsQuery } from "@qw/graphql";
import { GutterSeparator } from "@qw/ui";
import { useEffect, useMemo } from "react";
import { Draft } from "@/components/draft/draft";
import { useInboxQueryStates } from "@/providers/inbox-query-states-provider";
import { useConversation } from "@/providers/use-conversation";
import { ReplyPlacement, useDraftsCompose } from "@/providers/use-drafts-compose";

const THREAD_DRAFTS_ROWS_PER_PAGE = 50;

export const InlineDraftsCompose = () => {
  const { conversationId } = useInboxQueryStates();
  const { inboxConversation, isReadOnly } = useConversation();
  const { getInlineDraftsForConversation, openDrafts } = useDraftsCompose();
  const shouldLoadThreadDrafts = !isReadOnly && !!inboxConversation?.externalId;
  const { data, loading: areThreadDraftsLoading } = useInboxDraftsQuery({
    variables: {
      input: {
        threadId: inboxConversation?.externalId ?? "",
        page: 1,
        rowsPerPage: THREAD_DRAFTS_ROWS_PER_PAGE,
      },
    },
    skip: !shouldLoadThreadDrafts,
    fetchPolicy: "cache-and-network",
    notifyOnNetworkStatusChange: true,
  });
  const threadDraftIds = useMemo(
    () => (areThreadDraftsLoading ? [] : (data?.inboxDrafts.drafts.map((draft) => draft.id) ?? [])),
    [areThreadDraftsLoading, data]
  );
  const draftIds = isReadOnly ? [] : getInlineDraftsForConversation(conversationId);

  useEffect(() => {
    if (!shouldLoadThreadDrafts || !inboxConversation?.id || threadDraftIds.length === 0) {
      return;
    }

    openDrafts(threadDraftIds, {
      sourceConversationId: inboxConversation.id,
      placement: ReplyPlacement.inline,
      preserveExistingPlacement: true,
    });
  }, [inboxConversation?.id, openDrafts, shouldLoadThreadDrafts, threadDraftIds]);

  if (isReadOnly || draftIds.length === 0) {
    return null;
  }

  return (
    <>
      <div className="pl-(--gutter) pr-6 py-6 space-y-4">
        {draftIds.map((draftId) => (
          <Draft key={draftId} draftId={draftId} placement="inline" />
        ))}
      </div>
      <GutterSeparator />
    </>
  );
};
